import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { SectionLabel, TiltCard } from '../components/Effects';
import { ArrowIcon, DiscordIcon } from '../components/Icons';
import { SERVER_NEWS, type NewsItem } from '../data/news';

export function NewsPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [selectedTag, setSelectedTag] = useState<string>('All');
  const tags = ['All', 'Event', 'Server', 'Portal', 'Update', 'Economy'];

  // If URL has an article id, find it
  const currentArticle = id ? SERVER_NEWS.find((item) => item.id === id) : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const formatDate = (isoString: string) => {
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return isoString;
    }
  };

  // If reading a specific dispatch
  if (id && currentArticle) {
    return (
      <div className="news-page-wrap">
        <div className="news-page-hero">
          <div className="news-hero-content">
            <Link to="/news" className="news-back-link">
              <span className="back-arrow">←</span>
              <span>Back to All Dispatches</span>
            </Link>

            <div className="news-article-meta-header">
              <span className={`news-badge badge-${currentArticle.tag.toLowerCase()}`}>
                {currentArticle.tag.toUpperCase()}
              </span>
              <time className="news-date">{formatDate(currentArticle.date)}</time>
              {currentArticle.readTime && <span className="news-read-time">✦ {currentArticle.readTime}</span>}
              {currentArticle.author && <span className="news-author-name">By {currentArticle.author}</span>}
            </div>

            <h1 className="news-article-title">{currentArticle.title}</h1>
            <p className="news-article-lead">{currentArticle.body}</p>
          </div>
        </div>

        <article className="news-article-container section-pad">
          {currentArticle.image && (
            <div className="news-article-featured-img">
              <img src={currentArticle.image} alt={currentArticle.title} />
            </div>
          )}

          <div className="news-article-prose">
            {currentArticle.content?.map((paragraph, idx) => {
              // Handle list bullets if formatted with newlines
              if (paragraph.includes('\n•')) {
                const parts = paragraph.split('\n');
                return (
                  <div key={idx} className="news-prose-list-block">
                    <p>{parts[0]}</p>
                    <ul>
                      {parts.slice(1).map((item, bIdx) => (
                        <li key={bIdx}>{item.replace(/^•\s*/, '')}</li>
                      ))}
                    </ul>
                  </div>
                );
              }
              return <p key={idx}>{paragraph}</p>;
            })}
          </div>

          <div className="news-article-footer-cta">
            <div className="footer-cta-card">
              <h3>Ready to join our civilization?</h3>
              <p>Experience our live player economy, industrial patents, and cross-play survival on Java & Bedrock.</p>
              <div className="cta-actions">
                <a className="primary-btn enchant" href="https://apply.ugeappagesmp.online">
                  ✦ Apply for Whitelist <ArrowIcon />
                </a>
                <a className="discord-btn" href="https://discord.gg/hAqvebcSh3" target="_blank" rel="noreferrer">
                  <DiscordIcon /> Join Discord Community
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    );
  }

  // If unknown ID was provided, redirect to /news
  if (id && !currentArticle) {
    return (
      <div className="news-page-wrap not-found section-pad">
        <h2>Dispatch Not Found</h2>
        <p>The sovereign dispatch or announcement you requested does not exist or has been archived.</p>
        <Link to="/news" className="primary-btn enchant">Return to News Archive</Link>
      </div>
    );
  }

  // Default: Full News Index Page
  const filteredNews = selectedTag === 'All'
    ? SERVER_NEWS
    : SERVER_NEWS.filter(item => item.tag.toLowerCase() === selectedTag.toLowerCase());

  return (
    <div className="news-page-wrap">
      <div className="news-page-hero">
        <div className="news-hero-content">
          <SectionLabel index="ARCHIVES">SOVEREIGN CHRONICLE</SectionLabel>
          <h1>The Sovereign Gazette.<br /><em>State dispatches & chronicles.</em></h1>
          <p>
            Official dispatches, ministerial edicts, major server upgrades, seasonal contests, and economic milestones across Uge Appage SMP Season 3.
          </p>

          <div className="news-filter-bar">
            {tags.map(tag => (
              <button
                key={tag}
                type="button"
                className={`news-tag-btn ${selectedTag === tag ? 'active' : ''}`}
                onClick={() => setSelectedTag(tag)}
              >
                <span>{tag === 'All' ? '✦ All Dispatches' : `# ${tag}`}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="news-archive-section section-pad">
        <div className="news-grid">
          {filteredNews.map((item) => (
            <TiltCard key={item.id} className={`news-card ${item.pinned ? 'pinned-dispatch' : ''}`}>
              <div 
                className="news-card-inner clickable-card"
                onClick={() => navigate(`/news/${item.id}`)}
              >
                <div className="news-card-header">
                  <span className={`news-badge badge-${item.tag.toLowerCase()}`}>
                    {item.pinned ? '📌 ' : ''}{item.tag.toUpperCase()}
                  </span>
                  <time className="news-date" dateTime={item.date}>
                    {formatDate(item.date)}
                  </time>
                </div>

                <h2 className="news-card-title">{item.title}</h2>
                <p className="news-card-body">{item.body}</p>

                <div className="news-card-footer">
                  <span className="news-read-more">
                    <span>Read Full Dispatch</span>
                    <ArrowIcon />
                  </span>
                  {item.readTime && <small className="news-card-readtime">{item.readTime}</small>}
                </div>
              </div>
            </TiltCard>
          ))}
        </div>
      </section>
    </div>
  );
}
